/**
 * PosHardwareSettingsModal — Engranaje de configuración de impresora/gaveta en Caja.
 *
 * QUÉ HACE:
 *  - Botón de engranaje compacto (mismo icono `Settings` del slider de perfil) que
 *    abre un modal con la configuración de la impresora térmica y la gaveta.
 *  - Contenido por rol vía `usePermissions`:
 *    - Dueño/administrador → `mode="full"`: panel completo (papel, marca, transporte,
 *      auto-impresión de tienda, pruebas y opciones avanzadas).
 *    - Empleado con acceso a caja → `mode="cashier"`: solo tamaño de papel, marca,
 *      tipo de impresora y las pruebas de hardware (reconfigurar la térmica si algo falla).
 *  - Exporta también `PosHardwareSettingsDialog`: variante controlada (props
 *    `open`/`onClose`) SIN botón, usada globalmente (ej. desde el aviso de
 *    impresión sin impresora) con `mode="full"` para que cualquiera pueda
 *    configurar e imprimir sin depender de permisos.
 *
 * QUÉ INVOCA: usePermissions, UserSettingsPosHardware, Modal (components/ui).
 * QUÉ EXPORTA: PosHardwareSettingsModal (named) y PosHardwareSettingsDialog (named).
 *
 * QUÉ NO DEBE HACER:
 *  - No decide permisos por sí mismo más allá de dueño/admin vs resto (la vista
 *    "cashier" solo tiene sentido para quien ya está dentro de la ruta de caja).
 *  - No duplica la lógica de impresión (vive en lib/native/posHardware).
 */
import { useState } from "react";
import { Settings } from "lucide-react";
import { Modal } from "@/components/ui/Modal";
import { usePermissions } from "@/hooks";
import { UserSettingsPosHardware } from "./UserSettingsPosHardware";

interface PosHardwareSettingsDialogProps {
  open: boolean;
  onClose: () => void;
  /** "full" = panel completo; "cashier" = solo papel/marca/tipo + pruebas. */
  mode?: "full" | "cashier";
  canRunHardwareTests?: boolean;
}

/** Diálogo controlado (sin botón): abre la configuración POS desde cualquier ruta. */
export function PosHardwareSettingsDialog({
  open,
  onClose,
  mode = "full",
  canRunHardwareTests = true,
}: PosHardwareSettingsDialogProps) {
  const isFull = mode === "full";
  return (
    <Modal
      isOpen={open}
      onClose={onClose}
      title={isFull ? "POS, gaveta y seguridad de caja" : "Impresora"}
      subtitle={
        isFull
          ? "Configuración completa de la térmica, la gaveta y opciones del POS"
          : "Configura y conecta la impresora térmica (papel, marca y conexión)"
      }
      size="lg"
      compactOnMobile
      icon={<Settings size={18} className="text-primary-600" />}
    >
      <UserSettingsPosHardware
        canRunHardwareTests={canRunHardwareTests}
        mode={mode}
      />
    </Modal>
  );
}

export function PosHardwareSettingsModal() {
  const [open, setOpen] = useState(false);
  const { isOwner, isAdmin } = usePermissions();
  const isFull = isOwner || isAdmin;

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        title="Configuración de impresora y gaveta"
        aria-label="Configuración de impresora y gaveta"
        className="p-1.5 rounded-full text-gray-400 hover:text-gray-600 dark:text-slate-400 dark:hover:text-slate-200 hover:bg-gray-100 dark:hover:bg-slate-700 transition-colors shrink-0"
      >
        <Settings size={18} className="w-4 h-4 sm:w-[18px] sm:h-[18px]" />
      </button>

      <PosHardwareSettingsDialog
        open={open}
        onClose={() => setOpen(false)}
        mode={isFull ? "full" : "cashier"}
        canRunHardwareTests={isFull}
      />
    </>
  );
}
